import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PLATFORM_SHORT_NAME } from '../../branding';
import { MOBILE_TABS, activeTab, isMobileRoute } from '../../mobile/mobileNav';
import DesktopOnly from './DesktopOnly';

interface Props {
  children: React.ReactNode;
  /** The notification bell, passed in by Layout. Injected rather than imported
   *  because the bell lives inside Layout, and importing it here would make
   *  the two modules circular. */
  bell?: React.ReactNode;
}

/**
 * The phone shell: a compact top bar, the page, and a bottom tab bar.
 *
 * Bottom tabs rather than a hamburger drawer because the whole point of the
 * curated mobile build is that there are only five destinations — a drawer
 * would hide five items behind a tap for no reason, and thumbs reach the
 * bottom of a phone far more easily than the top-left corner.
 */
const MobileLayout: React.FC<Props> = ({ children, bell }) => {
  const { user, firmName, logout, canSwitchView, viewMode, setViewMode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const current = activeTab(location.pathname);
  const allowed = isMobileRoute(location.pathname);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-50">
      {/* Top bar. Title is the firm, not the product — same rule as the
          desktop sidebar: the user should see whose system they're in. */}
      <header
        className="bg-white border-b border-gray-200 px-4 flex items-center justify-between gap-3 shrink-0"
        style={{ paddingTop: 'max(0.625rem, env(safe-area-inset-top))', paddingBottom: '0.625rem' }}
      >
        <span className="font-bold text-blue-700 text-base tracking-tight truncate">
          {firmName || PLATFORM_SHORT_NAME}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {/* The same hat switch as the desktop sidebar, as a single pill —
              there is no room here for a two-button segmented control, and the
              label doubles as the indicator of which view you are in. */}
          {canSwitchView && (
            <button
              onClick={() => setViewMode(viewMode === 'admin' ? 'partner' : 'admin')}
              aria-label={`Switch to ${viewMode === 'admin' ? 'partner' : 'admin'} view`}
              className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${
                viewMode === 'admin'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-gray-100 text-gray-600 border-gray-200'
              }`}
            >
              {viewMode === 'admin' ? 'Admin' : 'Partner'}
            </button>
          )}
          {bell}
          <NavLink
            to="/profile"
            aria-label="Profile"
            className="w-8 h-8 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center"
          >
            {(user?.staffName || user?.email || '?').charAt(0).toUpperCase()}
          </NavLink>
        </div>
      </header>

      {/* The page. Padding is 1rem here, not the desktop's 1.5rem — at 375px
          those gutters are 13% of the screen. */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden p-4">
        {allowed ? children : <DesktopOnly onLogout={handleLogout} />}
      </main>

      <nav
        className="bg-white border-t border-gray-200 grid grid-cols-5 shrink-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {MOBILE_TABS.map((tab) => {
          const isActive = current?.to === tab.to;
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${
                isActive ? 'text-blue-600' : 'text-gray-400'
              }`}
            >
              <span className="text-xl leading-none" aria-hidden="true">
                {tab.icon}
              </span>
              <span>{tab.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileLayout;
